import React, { useEffect, useRef } from 'react';
import config from 'react-global-configuration';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';
import truncate from 'truncate-markup-string';

import {
  I18nProvider,
  CoreLink,
  Block,
} from '@uncinc/react-drupal-core';

import {
  WysiwygField,
  Container,
  SearchKit,
  SearchFacets,
  SearchPagination,
} from '@uncinc/uncinc-react-kitchen-sink';
import i18next from '../../config/i18next';
import SearchField from '../../components/SearchField';
import { BLOCK_CTA_VACANCY_BLOCK_ID, FILTER_ID_TYPE_VACANCY, VACANCY_SEARCH_QUERIES } from '../../config/constants';

import {
  Breadcrumbs,
} from '../../components';

import Header from '../../components/Header';
import SearchResults from '../../components/SearchResults';

import { SelectFacet } from './SelectFacet';
import translations from '../../config/i18next/locales/nl.json';

import * as query from './query.graphql';
import './style.scss';

const charLimit = 300;
const openTag = '<span>';
const closeTag = '</span>';
const prettyEllipsis = true;

const SearchItem = ({ item }) => {
  const searchItem = Immutable.fromJS(item);
  const pageTypes = translations.translation.facets.combined_type;
  const type = searchItem.getIn(['fields', 'combined_type', 0]);
  const typeLabel = pageTypes[type];
  const body = searchItem.getIn(['fields', 'body', 0], searchItem.getIn(['fields', 'description', 0]));

  return (
    <div className="Search__result">
      <CoreLink href={searchItem.getIn(['fields', 'url', 0])}>
        <div className="Badge Badge--primary">{typeLabel}</div>
        <h4 className="Search__result__title">{searchItem.getIn(['fields', 'title', 0], searchItem.getIn(['fields', 'name', 0]))}<span className="icon icon-arrow-right" /></h4>
        <div className="Search__result__url">{searchItem.getIn(['fields', 'url', 0])}</div>
        {body && (
          <div className="Search__result__body">
            <WysiwygField
              property="schema:text"
              html={truncate(body, charLimit, openTag, closeTag, prettyEllipsis)}
            />
          </div>
        )}
      </CoreLink>
    </div>
  );
};

const SearchHeader = ({ data }) => (
  <div className="Search__result__counter">{data.results.summary.total} Resultaten</div>
);

const SearchQueryActions = ({ data }) => {
  const query = data?.corp?.results?.summary?.query;
  const filterId = data?.corp?.results.summary?.appliedFilters[0]?.id;

  const searchingForVacancy = filterId === FILTER_ID_TYPE_VACANCY || VACANCY_SEARCH_QUERIES.includes(query);

  useEffect(() => {
    const searchVacancyCTA = document.querySelector('.Search__vacancyCTA');
    if (searchVacancyCTA && searchingForVacancy) {
      searchVacancyCTA.classList.add('Search__vacancyCTA--visible');
    } else if (searchVacancyCTA) {
      searchVacancyCTA.classList.remove('Search__vacancyCTA--visible');
    }
  }, [searchingForVacancy]);

  useEffect(() => {
    document.title = query ? `${query} - Zoeken` : 'Zoeken';
  }, [query]);

  return null;
};

const Search = () => {
  const rootClasses = useSelector((state) => state.getIn(['ui', 'rootClasses']));
  const language = useSelector((state) => state.getIn(['ui', 'language']));
  const currentLanguage = useSelector((state) => state.getIn(['ui', 'language']));
  const urlParams = new URLSearchParams(window.location.search);
  const searchQueryFromParams = urlParams.get('query');

  const scrollRef = useRef();

  return (
    <I18nProvider locale={language} i18next={i18next}>
      <div className={`App ${rootClasses.join(' ')}`} language={language}>
        <Header />
        <Container className="Search">
          <div className="Search__scrollHook" ref={scrollRef} />
          <div className="row">
            <div className="col-12">

              <div className="row">
                <div className="col-12">
                  <Breadcrumbs
                    data={[
                      { title: 'Search' },
                    ]}
                  />
                </div>
              </div>

              <h1 className="Search__title">Zoeken</h1>

              <div className="row">
                <div className="col-12 col-lg-10">
                  <div className="Search__vacancyCTA">
                    <Block
                      id={BLOCK_CTA_VACANCY_BLOCK_ID}
                    />
                  </div>
                </div>
              </div>

              {Object.keys(query).length > 0 && (
                <SearchKit
                  uri={config.get('APOLLO_SEARCH_URL')}
                  query={query}
                  defaultQuery={searchQueryFromParams}
                  defaultFilters={[
                    {
                      identifier: 'combined_language',
                      value: currentLanguage,
                    }
                  ]}
                >
                  {
                    (data, loading, ...props) => {
                      if (!data) {
                        return null;
                      }

                      return (
                        <>
                          <div className="Search__filters">
                            <SearchQueryActions data={data} />
                            <SearchField />
                            <SearchFacets data={data.corp} facets={[SelectFacet]} />
                          </div>
                          <SearchResults
                            data={data.corp}
                            renderItem={SearchItem}
                            renderHeader={SearchHeader}
                            scrollView={scrollRef}
                          />
                          <SearchPagination data={data.corp} />
                        </>
                      );
                    }
                  }
                </SearchKit>
              )}
            </div>
          </div>
        </Container>
      </div>
    </I18nProvider>
  );
};

export default Search;
